import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface WebSocketToken {
  websocket_token: string;
  expires_in: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketTokenService {
  private currentToken: WebSocketToken | null = null;
  private tokenSubject = new BehaviorSubject<WebSocketToken | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isRefreshing = false; // Prevent multiple simultaneous refresh requests
  private lastRefreshTime = 0; // Track last refresh time to prevent rapid requests
  private isInitialized = false; // Prevent multiple initializations

  constructor(private http: HttpClient) {}

  /**
   * Get a new WebSocket token from the server
   */
  getWebSocketToken(): Observable<WebSocketToken> {
    console.log('🔑 Requesting WebSocket token from server...');

    // Get JWT token from localStorage for authentication
    const userStr = localStorage.getItem('user');
    let jwtToken = null;

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        jwtToken = user.access_token;
        console.log('🔐 Using JWT token for WebSocket token request');
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }

    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };

    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    return this.http.post<WebSocketToken>(`${environment.apiUrl}/websocket-token`, {}, { headers });
  }

  /**
   * Get the current WebSocket token, refreshing if necessary
   */
  getCurrentToken(): Observable<WebSocketToken> {
    return new Observable((observer) => {
      // Prevent multiple initializations
      if (!this.isInitialized) {
        this.isInitialized = true;
      }

      if (this.isTokenValid()) {
        observer.next(this.currentToken!);
        observer.complete();
        return;
      }

      // Prevent rapid refresh requests
      const now = Date.now();
      if (now - this.lastRefreshTime < 10000) {
        // 10 second cooldown
        console.log('🔄 Token refresh requested too soon, using cached token or failing');
        if (this.currentToken) {
          observer.next(this.currentToken);
        } else {
          observer.error(new Error('Token refresh too frequent'));
        }
        observer.complete();
        return;
      }

      this.refreshToken().subscribe({
        next: (token) => {
          observer.next(token);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Refresh the WebSocket token
   */
  refreshToken(): Observable<WebSocketToken> {
    return new Observable((observer) => {
      // Prevent multiple simultaneous refresh requests
      if (this.isRefreshing) {
        console.log('🔄 Token refresh already in progress, waiting...');
        // Wait for the current refresh to complete
        const checkInterval = setInterval(() => {
          if (!this.isRefreshing && this.currentToken) {
            clearInterval(checkInterval);
            observer.next(this.currentToken);
            observer.complete();
          }
        }, 100);
        return;
      }

      this.isRefreshing = true;
      this.lastRefreshTime = Date.now();

      this.getWebSocketToken().subscribe({
        next: (token) => {
          console.log('✅ WebSocket token received:', token);
          this.currentToken = token;
          this.tokenSubject.next(token);
          this.scheduleTokenRefresh(token);
          this.isRefreshing = false;
          console.log('✅ WebSocket token refreshed successfully');
          observer.next(token);
          observer.complete();
        },
        error: (error) => {
          console.error('❌ Failed to refresh WebSocket token:', error);
          console.error('❌ Error details:', error.status, error.message);
          this.isRefreshing = false;
          observer.error(error);
        }
      });
    });
  }

  /**
   * Check if the current token is valid
   */
  private isTokenValid(): boolean {
    if (!this.currentToken) {
      return false;
    }

    // Calculate expiration time from expires_in (seconds from now)
    const expiresAt = new Date(Date.now() + this.currentToken.expires_in * 1000);
    const now = new Date();

    // Consider token expired if it expires in less than 30 seconds
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();
    const isValid = timeUntilExpiry > 30000; // 30 seconds buffer

    console.log(
      `🔑 Token validation: expires at ${expiresAt.toISOString()}, now ${now.toISOString()}, time until expiry: ${Math.round(
        timeUntilExpiry / 1000
      )}s, valid: ${isValid}`
    );

    return isValid;
  }

  /**
   * Schedule token refresh before expiration
   */
  private scheduleTokenRefresh(token: WebSocketToken): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Calculate expiration time from expires_in (seconds from now)
    const expiresAt = new Date(Date.now() + token.expires_in * 1000);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();

    // Refresh token 1 minute before expiration
    const refreshTime = Math.max(timeUntilExpiry - 60000, 1000);

    console.log(
      `🔄 Scheduling token refresh in ${Math.round(
        refreshTime / 1000
      )} seconds (expires at ${expiresAt.toISOString()})`
    );

    this.refreshTimer = setTimeout(() => {
      console.log('🔄 WebSocket token expiring soon, refreshing...');
      this.refreshToken().subscribe({
        next: () => console.log('✅ WebSocket token refreshed successfully'),
        error: (error) => console.error('❌ Failed to refresh WebSocket token:', error)
      });
    }, refreshTime);
  }

  /**
   * Clear the current token
   */
  clearToken(): void {
    this.currentToken = null;
    this.tokenSubject.next(null);
    this.isRefreshing = false;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Get token as observable
   */
  getToken$(): Observable<WebSocketToken | null> {
    return this.tokenSubject.asObservable();
  }

  /**
   * Get current token synchronously
   */
  getTokenSync(): WebSocketToken | null {
    return this.currentToken;
  }
}
